import React, { useEffect, useRef, useState } from 'react';
import { User, LogOut, BarChart3, FileText, Settings } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import SettingsModal from './SettingsModal';
import { useGetSettingsQuery } from '../store/services/settingsApi';
import { logout } from '../store/slices/authSlice';

const Header: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { data: settingsResponse } = useGetSettingsQuery();

  const currentPath = location.pathname;
  const settingsData = settingsResponse?.data;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setMenuOpen(false);
    dispatch(logout());
    toast.success('Logout successful'); 
    navigate('/admin/login');
  };

  const handleSettings = () => {
    setMenuOpen(false);
    setSettingsModalOpen(true);
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 py-3 sm:h-16 sm:flex-nowrap sm:py-0">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <img src='/images/logo.jpg' alt='iimi' className='w-[76px] object-contain sm:w-[90px]' />
          </div>

          {/* Centered Navigation Menu */}
          <div className="order-3 flex w-full justify-center sm:order-2 sm:flex-1 sm:w-auto">
            <div className="flex w-full max-w-sm items-center justify-center gap-2 sm:w-auto sm:max-w-none sm:gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className={`flex flex-1 items-center justify-center space-x-1 rounded-md px-2 py-2 text-xs font-medium transition duration-200 sm:flex-none sm:space-x-2 sm:px-3 sm:text-sm ${
                  currentPath.includes('/admin/dashboard')
                    ? 'bg-[#c31200] text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>

              <button
                onClick={() => navigate('/admin/applications')}
                className={`flex flex-1 items-center justify-center space-x-1 rounded-md px-2 py-2 text-xs font-medium transition duration-200 sm:flex-none sm:space-x-2 sm:px-3 sm:text-sm ${
                  currentPath.includes('/admin/applications')
                    ? 'bg-[#c31200] text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Applications</span>
              </button>
            </div>
          </div>

          {/* User Menu */}
          <div className="relative order-2 flex items-center flex-shrink-0 sm:order-3" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((current) => !current)}
              className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-gray-600 transition duration-200 hover:bg-gray-200"
            >
              {settingsData?.profilePic?.url ? (
                <img src={settingsData.profilePic.url} alt="Admin avatar" className="h-full w-full object-cover" />
              ) : (
                <User className="h-4 w-4" />
              )}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-12 z-50 w-36 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
                <button
                  type="button"
                  onClick={handleSettings}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <SettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        settings={settingsData}
      />
    </nav>
  );
};

export default Header;
